// src/pages/Favorites.jsx
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getCanonicalForumThreadPath } from "../features/posts/canonicalThreadNavigation";
import { useAuth } from "../context/AuthContext";
import { useAnime } from "../context/AnimeContext";
import Navbar from "../components/Navbar";
import {
  ArrowLeft,
  Search,
  SlidersHorizontal,
  Heart,
  Trash2,
  ChevronDown,
  LayoutGrid,
  List,
  ExternalLink,
  MessageCircle,
  Image as ImageIcon,
} from "lucide-react";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (process.env.NODE_ENV === "development" ? "http://localhost:5000" : "");

function Favorites() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { animeList, favorites, removeFromFavorites } = useAnime();

  const [localLoading, setLocalLoading] = useState(true);

  // Saved hub section: anime | threads | art
  const [section, setSection] = useState("anime"); // anime | threads | art

  // Threads + Art state
  const [savedThreads, setSavedThreads] = useState([]);
  const [savedArt, setSavedArt] = useState([]);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [artLoading, setArtLoading] = useState(false);

  // UI state
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent"); // recent | title-asc | title-desc
  const [layout, setLayout] = useState("grid"); // grid | compact

  useEffect(() => {
    if (user && animeList.length > 0) setLocalLoading(false);
  }, [user, animeList]);

  // --- Fetch saved threads / art (safe fallback if endpoint not ready) ---
  useEffect(() => {
    if (!user) return;

    const controller = new AbortController();

    const fetchSavedThreads = async () => {
      setThreadsLoading(true);
      try {
        const res = await fetch(`${API_BASE}/saved/threads`, {
          credentials: "include",
          signal: controller.signal,
        });
        if (!res.ok) {
          setSavedThreads([]);
          return;
        }
        const data = await res.json();
        setSavedThreads(Array.isArray(data) ? data : data?.threads || []);
      } catch {
        // endpoint not ready / network issue -> just show empty
        setSavedThreads([]);
      } finally {
        setThreadsLoading(false);
      }
    };

    const fetchSavedArt = async () => {
      setArtLoading(true);
      try {
        const res = await fetch(`${API_BASE}/saved/art`, {
          credentials: "include",
          signal: controller.signal,
        });
        if (!res.ok) {
          setSavedArt([]);
          return;
        }
        const data = await res.json();
        setSavedArt(Array.isArray(data) ? data : data?.art || []);
      } catch {
        setSavedArt([]);
      } finally {
        setArtLoading(false);
      }
    };

    // fetch once when logged in
    fetchSavedThreads();
    fetchSavedArt();

    return () => controller.abort();
  }, [user]);

  // Image fallback logic (anime)
  const getImageSrc = (anime) => {
    const fallback = String(anime?.Title || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/gi, "")
      .trim();

    const url = anime?.PhotoUrl;
    if (url && !url.includes("example.com")) {
      return url.startsWith("http")
        ? url
        : url.startsWith("/")
          ? url
          : `/${url.replace(/^\/+/, "")}`;
    }
    return `/images/${fallback}.jpg`;
  };

  // --------- Saved Anime mapping (unchanged behavior) ---------
  const favoriteAnime = useMemo(() => {
    if (!Array.isArray(favorites) || favorites.length === 0) return [];
    const map = new Map(animeList.map((a) => [String(a?.AnimeID), a]));
    return favorites
      .map((fav) => {
        const anime = map.get(String(fav?.animeId));
        return anime ? { anime, fav } : null;
      })
      .filter(Boolean);
  }, [favorites, animeList]);

  // --------- Normalized data per section (so UI is consistent) ---------
  const normalizedItems = useMemo(() => {
    if (section === "anime") {
      return favoriteAnime.map(({ anime, fav }) => ({
        type: "anime",
        id: String(fav?.animeId),
        title: anime?.Title || "Untitled",
        description: anime?.Description || anime?.Synopsis || "Saved to your collection.",
        image: getImageSrc(anime),
        metaLeft: `ID: ${fav?.animeId}`,
        href: `/anime/${fav?.animeId}`,
        raw: { anime, fav },
      }));
    }

    if (section === "threads") {
      return (Array.isArray(savedThreads) ? savedThreads : []).map((t) => ({
        type: "threads",
        id: String(t?.id || t?.threadId || t?._id || ""),
        title: t?.title || "Untitled thread",
        description: t?.body || t?.content || t?.text || "",
        image: t?.coverImage || t?.image || "", // optional
        metaLeft: t?.author_username
          ? `by ${t.author_username}`
          : t?.author
            ? `by ${t.author}`
            : "Saved thread",
        href:
          t?.id || t?.threadId || t?._id || t?.post_id
            ? getCanonicalForumThreadPath(t)
            : "",
        raw: t,
      }));
    }

    // art
    return (Array.isArray(savedArt) ? savedArt : []).map((a) => ({
      type: "art",
      id: String(a?.id || a?.artId || a?._id || ""),
      title: a?.title || "Untitled art",
      description: a?.description || a?.caption || "",
      image:
        a?.imageUrl ||
        a?.image ||
        a?.url ||
        "", // expected
      metaLeft: a?.artist_username
        ? `by ${a.artist_username}`
        : a?.artist
          ? `by ${a.artist}`
          : "Saved art",
      href: a?.id || a?.artId || a?._id ? `/fanart/${a.id || a.artId || a._id}` : "",
      raw: a,
    }));
  }, [section, favoriteAnime, savedThreads, savedArt, animeList, favorites]);

  // --------- Filtering + sorting (shared) ---------
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...normalizedItems];

    if (q) {
      list = list.filter((it) => {
        const title = String(it?.title || "").toLowerCase();
        const desc = String(it?.description || "").toLowerCase();
        return title.includes(q) || desc.includes(q);
      });
    }

    if (sortBy === "title-asc") {
      list.sort((a, b) => String(a.title || "").localeCompare(String(b.title || "")));
    } else if (sortBy === "title-desc") {
      list.sort((a, b) => String(b.title || "").localeCompare(String(a.title || "")));
    } else {
      // recent: keep backend/order as-is (anime preserves favorites order already)
    }

    return list;
  }, [normalizedItems, query, sortBy]);

  const shownCount = filtered.length;

  const totalCount = useMemo(() => {
    if (section === "anime") return favorites?.length || 0;
    if (section === "threads") return savedThreads?.length || 0;
    return savedArt?.length || 0;
  }, [section, favorites, savedThreads, savedArt]);

  const isSectionLoading =
    localLoading ||
    (section === "threads" && threadsLoading) ||
    (section === "art" && artLoading);

  // ---------- Removal handlers ----------
  const removeSavedThread = async (threadId) => {
    const id = String(threadId || "");
    if (!id) return;
    // optimistic UI
    setSavedThreads((prev) =>
      (Array.isArray(prev) ? prev : []).filter((t) => String(t?.id || t?.threadId || t?._id) !== id)
    );

    try {
      await fetch(`${API_BASE}/saved/threads/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
    } catch {
      // ignore: keep optimistic state
    }
  };

  const removeSavedArt = async (artId) => {
    const id = String(artId || "");
    if (!id) return;
    setSavedArt((prev) =>
      (Array.isArray(prev) ? prev : []).filter((a) => String(a?.id || a?.artId || a?._id) !== id)
    );

    try {
      await fetch(`${API_BASE}/saved/art/${id}`, {
        method: "DELETE",
        credentials: "include",
      });
    } catch {
      // ignore
    }
  };

  // Guest fallback
  if (!user) {
    return (
      <div className="min-h-screen bg-[#0A1020] text-white">
        <Navbar />
        <div className="mx-auto max-w-3xl px-4 py-16">
          <div className="rounded-[28px] border border-white/10 bg-white/5 shadow-[0_18px_55px_rgba(0,0,0,0.45)] p-8">
            <p className="text-xl font-semibold text-white/90">Log in to view saved items</p>
            <p className="mt-2 text-sm text-white/60 leading-relaxed">
              Your saved anime, threads, and art are tied to your account.
            </p>

            <Link
              to="/login"
              className="mt-6 inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white/90 hover:bg-white/15 transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Go to Login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const SectionIcon = section === "anime" ? Heart : section === "threads" ? MessageCircle : ImageIcon;
  const sectionLabel =
    section === "anime" ? "Anime" : section === "threads" ? "Threads" : "Art";

  return (
    <div className="min-h-screen bg-[#0A1020] text-white">
      <Navbar />

      {/* Page frame */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 pt-6 pb-10">
        {/* Floating glass controls (top-left only, no banner) */}
        <div className="flex items-start justify-between gap-3">
          {/* Left cluster */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Back */}
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white/90 hover:bg-white/15 transition-colors shadow-[0_12px_30px_rgba(0,0,0,0.25)]"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>

            {/* Floating glass bar */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-[22px] border border-white/10 bg-white/5 backdrop-blur-md px-3 py-3 shadow-[0_18px_55px_rgba(0,0,0,0.35)]">
              {/* Section switcher */}
              <div className="flex items-center gap-1 rounded-2xl border border-white/10 bg-[#0B1430] p-1 w-full sm:w-auto">
                <SegButton
                  active={section === "anime"}
                  onClick={() => setSection("anime")}
                  icon={<Heart className="h-4 w-4" />}
                  label="Anime"
                />
                <SegButton
                  active={section === "threads"}
                  onClick={() => setSection("threads")}
                  icon={<MessageCircle className="h-4 w-4" />}
                  label="Threads"
                />
                <SegButton
                  active={section === "art"}
                  onClick={() => setSection("art")}
                  icon={<ImageIcon className="h-4 w-4" />}
                  label="Art"
                />
              </div>

              {/* Search */}
              <div className="relative w-full sm:w-[320px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/45" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={`Search saved ${sectionLabel.toLowerCase()}…`}
                  className={[
                    "w-full rounded-2xl border bg-[#0B1430] px-10 py-2.5 text-sm text-white/90 outline-none",
                    "placeholder:text-white/35",
                    "border-white/10 focus:border-white/20",
                  ].join(" ")}
                />
              </div>

              {/* Sort */}
              <div className="relative w-full sm:w-[240px]">
                <SlidersHorizontal className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/45" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className={[
                    "appearance-none w-full rounded-2xl border bg-[#0B1430] pl-10 pr-10 py-2.5 text-sm text-white/90 outline-none",
                    "border-white/10 focus:border-white/20",
                  ].join(" ")}
                >
                  <option value="recent">Sort: Saved order</option>
                  <option value="title-asc">Sort: Title (A → Z)</option>
                  <option value="title-desc">Sort: Title (Z → A)</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/45" />
              </div>

              {/* Layout */}
              <button
                type="button"
                onClick={() => setLayout((v) => (v === "grid" ? "compact" : "grid"))}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white/90 hover:bg-white/15 transition-colors shrink-0"
                title="Toggle layout"
              >
                {layout === "grid" ? (
                  <>
                    <List className="h-4 w-4" />
                    <span className="hidden sm:inline">Compact</span>
                  </>
                ) : (
                  <>
                    <LayoutGrid className="h-4 w-4" />
                    <span className="hidden sm:inline">Grid</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Right: small saved count only */}
          <div className="hidden sm:flex items-center gap-2 text-xs text-white/60">
            <SectionIcon className="h-4 w-4 text-[#B9F6D0]/80" />
            <span className="text-white/85 font-semibold">{totalCount}</span>
            <span>saved</span>
          </div>
        </div>

        {/* Content */}
        <div className="mt-8">
          {isSectionLoading ? (
            <div className="mx-auto max-w-2xl rounded-2xl border border-white/10 bg-white/5 p-6 text-center text-white/70 shadow-[0_18px_55px_rgba(0,0,0,0.35)]">
              Loading…
            </div>
          ) : totalCount > 0 ? (
            <>
              {layout === "grid" ? (
                <div className="grid gap-5 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {filtered.map((it) => (
                    <div
                      key={`${it.type}:${it.id}`}
                      className="group rounded-[28px] border border-white/10 bg-white/5 shadow-[0_16px_50px_rgba(0,0,0,0.35)] hover:bg-white/10 transition-colors overflow-hidden"
                    >
                      {/* Image / Preview */}
                      <div className="relative">
                        {it.image ? (
                          <img
                            src={it.image}
                            alt={it.title}
                            className="h-64 w-full object-cover"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = "https://via.placeholder.com/300x450?text=No+Image";
                            }}
                          />
                        ) : (
                          <div className="h-64 w-full bg-[#0B1430] grid place-items-center border-b border-white/10">
                            <div className="flex flex-col items-center gap-2 text-white/60">
                              {it.type === "threads" ? (
                                <MessageCircle className="h-6 w-6" />
                              ) : it.type === "art" ? (
                                <ImageIcon className="h-6 w-6" />
                              ) : (
                                <Heart className="h-6 w-6" />
                              )}
                              <span className="text-xs">Preview</span>
                            </div>
                          </div>
                        )}

                        {/* Top actions */}
                        <div className="absolute top-3 left-3 right-3 flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3 py-1 text-[11px] text-white/85">
                            {it.type === "anime" ? (
                              <Heart className="h-3.5 w-3.5 text-[#B9F6D0]/80" />
                            ) : it.type === "threads" ? (
                              <MessageCircle className="h-3.5 w-3.5 text-[#B9F6D0]/80" />
                            ) : (
                              <ImageIcon className="h-3.5 w-3.5 text-[#B9F6D0]/80" />
                            )}
                            Saved
                          </span>

                          <button
                            onClick={() => {
                              if (it.type === "anime") return removeFromFavorites(it.id);
                              if (it.type === "threads") return removeSavedThread(it.id);
                              return removeSavedArt(it.id);
                            }}
                            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/30 px-3 py-1 text-[11px] font-semibold text-white/85 hover:bg-black/45 transition-colors"
                            title="Remove"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Remove
                          </button>
                        </div>
                      </div>

                      {/* Body */}
                      <div className="p-4">
                        <h2 className="text-base font-semibold text-white/92 leading-snug line-clamp-2">
                          {it.title}
                        </h2>

                        <p className="mt-2 text-xs text-white/55 line-clamp-2">
                          {it.description || "Saved to your collection."}
                        </p>

                        <div className="mt-4 flex items-center justify-between gap-3">
                          {it.href ? (
                            <Link
                              to={it.href}
                              className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-3 py-2 text-xs font-semibold text-white/90 hover:bg-white/15 transition-colors"
                            >
                              View
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Link>
                          ) : (
                            <button
                              type="button"
                              className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-3 py-2 text-xs font-semibold text-white/60 cursor-not-allowed"
                              disabled
                            >
                              View
                              <ExternalLink className="h-3.5 w-3.5" />
                            </button>
                          )}

                          <span className="text-[11px] text-white/45 truncate">{it.metaLeft}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                // COMPACT LIST
                <div className="space-y-3">
                  {filtered.map((it) => (
                    <div
                      key={`${it.type}:${it.id}`}
                      className="group flex items-center gap-4 rounded-[28px] border border-white/10 bg-white/5 shadow-[0_16px_50px_rgba(0,0,0,0.35)] hover:bg-white/10 transition-colors p-3 sm:p-4"
                    >
                      {it.image ? (
                        <img
                          src={it.image}
                          alt={it.title}
                          className="h-20 w-14 rounded-2xl object-cover border border-white/10"
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "https://via.placeholder.com/140x200?text=No+Image";
                          }}
                        />
                      ) : (
                        <div className="h-20 w-14 rounded-2xl border border-white/10 bg-[#0B1430] grid place-items-center text-white/60">
                          {it.type === "threads" ? (
                            <MessageCircle className="h-5 w-5" />
                          ) : it.type === "art" ? (
                            <ImageIcon className="h-5 w-5" />
                          ) : (
                            <Heart className="h-5 w-5" />
                          )}
                        </div>
                      )}

                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-white/92 truncate">{it.title}</p>
                        <p className="mt-1 text-xs text-white/55 line-clamp-2">
                          {it.description || "Saved to your collection."}
                        </p>

                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          {it.href ? (
                            <Link
                              to={it.href}
                              className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-3 py-2 text-xs font-semibold text-white/90 hover:bg-white/15 transition-colors"
                            >
                              View
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Link>
                          ) : (
                            <button
                              type="button"
                              className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-3 py-2 text-xs font-semibold text-white/60 cursor-not-allowed"
                              disabled
                            >
                              View
                              <ExternalLink className="h-3.5 w-3.5" />
                            </button>
                          )}

                          <button
                            onClick={() => {
                              if (it.type === "anime") return removeFromFavorites(it.id);
                              if (it.type === "threads") return removeSavedThread(it.id);
                              return removeSavedArt(it.id);
                            }}
                            className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-[#0B1430] px-3 py-2 text-xs font-semibold text-white/88 hover:bg-black/30 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Remove
                          </button>

                          <span className="text-[11px] text-white/45 truncate">{it.metaLeft}</span>
                        </div>
                      </div>

                      <div className="hidden sm:flex items-center gap-2 text-xs text-white/55">
                        {it.type === "anime" ? (
                          <Heart className="h-4 w-4 text-[#B9F6D0]/80" />
                        ) : it.type === "threads" ? (
                          <MessageCircle className="h-4 w-4 text-[#B9F6D0]/80" />
                        ) : (
                          <ImageIcon className="h-4 w-4 text-[#B9F6D0]/80" />
                        )}
                        Saved
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty state for filter */}
              {totalCount > 0 && shownCount === 0 && (
                <div className="mt-10 mx-auto max-w-2xl rounded-2xl border border-white/10 bg-white/5 p-6 text-center text-white/70 shadow-[0_18px_55px_rgba(0,0,0,0.35)]">
                  No saved items match your search.
                </div>
              )}
            </>
          ) : (
            <div className="mx-auto max-w-3xl rounded-[34px] border border-white/10 bg-white/5 shadow-[0_18px_55px_rgba(0,0,0,0.35)] p-8 text-center">
              <p className="text-xl font-semibold text-white/92">Nothing saved yet</p>
              <p className="mt-2 text-sm text-white/60 leading-relaxed">
                Save anime, threads, and fan art to build your personal collection.
              </p>

              <Link
                to="/"
                className="mt-6 inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-4 py-2.5 text-sm font-semibold text-white/90 hover:bg-white/15 transition-colors"
              >
                Explore
                <ExternalLink className="h-4 w-4" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SegButton({ active, onClick, icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "flex-1 sm:flex-none inline-flex items-center justify-center gap-2",
        "rounded-2xl px-3 py-2 text-xs font-semibold transition-colors",
        active
          ? "bg-white/12 text-white border border-white/10 shadow-[0_10px_25px_rgba(0,0,0,0.25)]"
          : "text-white/70 hover:text-white/90 hover:bg-white/5",
      ].join(" ")}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
      <span className="sm:hidden">{label}</span>
    </button>
  );
}

export default Favorites;

